import React, { useMemo, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import idx from "idx";
import Gettext from "../common/Gettext";
import { Fade } from "../common/Animations";
import { fontSize, rtl } from "../common/styled-mixins";
import MobilePopup from "../TradeMobile/Popup";
import DesktopPopup from "../TradeModals/Modal";
import { gaReducer } from "../GA";
import Money from "../Money";

import Button from "../Button";
import ShieldIcon from "./ShieldIcon";
import shieldImage from "./shieldImage.svg";
import protectedImage from "./protected.svg";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  min-height: 100%;
  margin: 0 auto;
  padding: 15px;
  direction: ${({ theme }) => theme.direction};

  @media (min-width: 960px) {
    padding: 35px;
  }
`;

const Content = styled.div`
  margin-bottom: 17px;
`;

const BottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: inline-block;
  min-width: 171px;
  margin-bottom: 8px;

  > div {
    font-size: 18px !important;
  }
`;

const ButtonText = styled.span`
  font-weight: bold;
  line-height: 20px;
  font-size: ${fontSize(12)};
  vertical-align: middle;
`;

const TimerContainer = styled.div`
  margin-bottom: 10px;
  text-align: center;
  font-size: ${fontSize(14)};
  color: ${({ theme }) => theme.compensationSubtitle};
`;

const Title = styled.div`
  text-align: center;
  font-size: 18px;
  font-weight: 500;
  line-height: 21px;
  color: ${({ type, theme }) => {
    switch (type) {
      case "success":
        return theme.compensationSuccessTitle;
      case "refill":
        return theme.compensationRefillTitle;
      case "compensate":
        return "#E74C3C";
      default:
        return null;
    }
  }};
`;

const ImageContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  margin-top: 17px;
`;

const ShieldImage = styled.img`
  position: relative;
  z-index: 2;

  :first-of-type {
    width: 43px;
    margin-right: -10px;
    z-index: 1;
  }

  :last-of-type {
    width: 43px;
    margin-left: -10px;
    z-index: 1;
  }
`;

const Subtitle = styled.div`
  margin: 17px 0;
  font-size: ${fontSize(14)};
  line-height: 1.71;
  font-weight: bold;
  color: ${({ theme }) => theme.compensationSubtitle};
  text-align: center;
`;

const ShieldsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  height: 12px;

  > svg {
    ${rtl("margin-right", "4px")};

    :last-of-type {
      ${rtl("margin-right", 0)};
    }
  }
`;

const DealsCountContainer = styled.div`
  @media (max-width: 959px) and (orientation: landscape) {
    display: flex;
    justify-content: center;
  }
`;

const DealsCountText = styled.div`
  margin-bottom: 8px;
  text-align: center;
  font-size: ${fontSize(12)};
  color: #919191;

  @media (max-width: 959px) and (orientation: landscape) {
    margin-right: 6px;
  }
`;

const ActionButton = styled.span`
  font-size: ${fontSize(12)};
  color: ${({ theme }) => theme.compensationActionButton};
  cursor: pointer;
  text-decoration: underline;

  :hover {
    text-decoration: none;
  }
`;

const AmountOfDealsBlock = styled.div`
  margin: 16px 0 24px;
  font-size: 22px;
  line-height: 1.2;
  font-weight: 700;
  color: ${({ theme }) => theme.compensationSubtitle};
  text-align: center;
`;

const RefillDescription = styled.p`
  margin-top: 24px;
  font-size: ${fontSize(12)};
  line-height: 1.2;
  color: #919191;
  text-align: center;
`;

const AccountType = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  padding: 10px;
  background: ${({ isReal }) => (isReal ? "#27c191" : "#ffcc00")};
  border-radius: 5px 0 5px 0;
  font-size: ${fontSize(12)};
  color: ${({ isReal }) => (isReal ? "white" : "black")};
  text-transform: uppercase;

  @media (max-width: 959px) {
    border-radius: 0;
  }
`;

export const Shields = props => {
  try {
    const MAX_COUNT = 5;
    const available = Array.from({ length: props.availableCompensations });
    const used = Array.from({ length: props.usedCompensations });
    const total =
      Number(props.availableCompensations) + Number(props.usedCompensations);
    const shields = available
      .map((_, key) => <ShieldIcon isActive={true} key={key} />)
      .concat(
        used.map((_, key) => (
          <ShieldIcon isActive={false} key={key + MAX_COUNT} />
        ))
      );
    if (total > MAX_COUNT) {
      return shields.slice(0, MAX_COUNT);
    } else {
      return <React.Fragment>{shields}</React.Fragment>;
    }
  } catch (error) {
    console.log(error);
    return null;
  }
};

const PopupLayout = props => {
  return props.isDesktop ? (
    <DesktopPopup
      key={props.deal && props.deal.id}
      onDismiss={props.closeModal}
      disableOutside={props.disableOutside}
    >
      {props.children}
    </DesktopPopup>
  ) : (
    <Fade in={true}>
      {({ style }) => (
        <MobilePopup
          title={null}
          onClick={props.closeModal}
          style={style}
          darkMode={true}
          isCompensation={true}
        >
          {props.children}
        </MobilePopup>
      )}
    </Fade>
  );
};

const Timer = ({ time, compensateCountdown }) => {
  const duration = moment.duration(time, "seconds");
  const h = duration.hours() < 10 ? `0${duration.hours()}` : duration.hours();
  const m =
    duration.minutes() < 10 ? `0${duration.minutes()}` : duration.minutes();
  const s =
    duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

  if (compensateCountdown >= 3600) {
    return `${h}:${m}:${s}`;
  }

  return `${m}:${s}`;
};

type Props = {
  onDismiss: () => void,
  deal?: deal,
  successPopup?: boolean,
  onCompensation?: () => void,
  loading?: boolean,
  currentTime?: number,
  time?: number,
  popupType: "compensate" | "success" | "refill",
  onDetails?: () => void,
  disableOutside: boolean,
  compensateCountdown?: number,
  isInfo?: boolean,
  activeAccount: Object
};

export const comps = (compensations, type, typeAccount) => {
  if (
    compensations != null &&
    compensations[typeAccount] != null &&
    typeAccount
  ) {
    return compensations[typeAccount].items.reduce((acc, item) => {
      return type === "available" ? acc + item.available : acc + item.used;
    }, 0);
  }
};

const Popup = React.memo((props: Props) => {
  const {
    onDismiss,
    onCompensation,
    popupType,
    loading,
    deal,
    compensations,
    time,
    isDesktop,
    compensationCreated,
    onCompensationCreated,
    onDetails,
    disableOutside,
    compensateCountdown,
    isInfo,
    activeAccount
  } = props;

  const [spinner, setSpinner] = useState(false);
  const isCompensationCreated =
    popupType === "refill" && !isInfo && compensationCreated != null;

  const typeAccount =
    popupType === "refill" && !isInfo && compensationCreated != null
      ? compensationCreated.payload.real === 1
        ? "real"
        : "demo"
      : activeAccount && activeAccount.type;
  const availableCompensations = useMemo(
    () => comps(compensations, "available", typeAccount),
    [compensations, typeAccount]
  );
  const usedCompensations = useMemo(
    () => comps(compensations, "used", typeAccount),
    [compensations, typeAccount]
  );

  const amount_limit =
    idx(compensations, _ => _[typeAccount].items[0].amount_limit) || 0;

  const closeModal = async type => {
    if (popupType === "compensate") {
      type === "timeout"
        ? gaReducer("COMPENSATE_TIMEOUT", { amount: Math.abs(deal.profit) })
        : gaReducer("CANCEL_COMPENSATE", { amount: Math.abs(deal.profit) });
    }
    if (popupType === "refill" && compensationCreated != null) {
      if (type === "simpleClose") {
        onDismiss();
        return;
      }
      setSpinner(true);
      await onCompensationCreated();
      setSpinner(false);
    }
    onDismiss();
  };

  if (popupType === "compensate" && time < 0) {
    closeModal("timeout");
  }

  const titleText = () => {
    switch (popupType) {
      case "success":
        return <Gettext>Your trade is covered!</Gettext>;
      case "compensate":
        return <Gettext>Your trade was unsuccessful!</Gettext>;
      default:
        return null;
    }
  };

  const subtitle = () => {
    switch (popupType) {
      case "success":
        return (
          <Subtitle>
            {/* prettier-ignore */}
            <Gettext>
              You've used {usedCompensations} out
              of {availableCompensations + usedCompensations} risk-free
              trades. Now the money will be credited back to your account.
            </Gettext>
            <br />
            <Gettext>Please be more careful in the future!</Gettext>
          </Subtitle>
        );
      case "compensate":
        return (
          <Subtitle>
            {/* prettier-ignore */}
            <Gettext>
              Do you want to apply a risk-free trade to cover your
              losses of {<Money>{Math.abs(deal.profit)}</Money>}?
            </Gettext>
          </Subtitle>
        );
      default:
        return null;
    }
  };

  return (
    <PopupLayout
      closeModal={
        isCompensationCreated && compensationCreated.payload.real === 1
          ? () => closeModal("simpleClose")
          : closeModal
      }
      isDesktop={isDesktop}
      disableOutside={
        disableOutside ||
        (isCompensationCreated && compensationCreated.payload.real === 1)
      }
      deal={deal}
    >
      {isCompensationCreated && (
        <AccountType isReal={typeAccount === "real"}>
          {typeAccount === "demo" ? (
            <Gettext>Demo account</Gettext>
          ) : (
            <Gettext>Real account</Gettext>
          )}
        </AccountType>
      )}
      <Container>
        <Content>
          {popupType !== "refill" && (
            <Title type={popupType}>{titleText()}</Title>
          )}
          {popupType === "refill" && (
            <AmountOfDealsBlock>
              <Gettext>
                Hey! Don't worry about making mistakes from now!
              </Gettext>
              <br />
              {/* prettier-ignore */}
              <Gettext>
                We will return your money for the next {availableCompensations} unlucky trades.
              </Gettext>
            </AmountOfDealsBlock>
          )}
          {popupType !== "refill" && (
            <ImageContainer>
              {popupType === "success" ? (
                <img src={protectedImage} alt="" />
              ) : (
                <React.Fragment>
                  <ShieldImage src={shieldImage} />
                  <ShieldImage src={shieldImage} />
                  <ShieldImage src={shieldImage} />
                </React.Fragment>
              )}
            </ImageContainer>
          )}
          {subtitle()}
          {popupType === "refill" ? (
            <DealsCountContainer>
              <ShieldsContainer>
                <Shields
                  availableCompensations={availableCompensations}
                  usedCompensations={0}
                />
              </ShieldsContainer>
            </DealsCountContainer>
          ) : (
            <DealsCountContainer>
              <DealsCountText>
                <Gettext>Available risk-free trades:</Gettext>
              </DealsCountText>
              <ShieldsContainer>
                <Shields
                  availableCompensations={availableCompensations}
                  usedCompensations={usedCompensations}
                />
              </ShieldsContainer>
            </DealsCountContainer>
          )}
          {popupType === "refill" && (
            <RefillDescription>
              <Gettext>
                This is the number of risk-free trades you can apply to cover
                your losses in case of bad deals.
              </Gettext>
              <br />
              {/* prettier-ignore */}
              <Gettext>
                We can refund any trades worth up to <Money>{amount_limit}</Money>. Don't invest more until you get
                more confident.
              </Gettext>
            </RefillDescription>
          )}
        </Content>
        <BottomContainer>
          <ButtonContainer>
            <Button
              color="green"
              isIron
              newStyle
              onClick={
                popupType === "success" || popupType === "refill"
                  ? closeModal
                  : onCompensation
              }
              showSpinner={loading || spinner}
            >
              <ButtonText>
                {popupType === "success" ? (
                  <Gettext>Trade</Gettext>
                ) : popupType === "refill" ? (
                  <Gettext>Good luck</Gettext>
                ) : (
                  <Gettext>Apply</Gettext>
                )}
              </ButtonText>
            </Button>
          </ButtonContainer>
          {popupType === "compensate" && (
            <TimerContainer>
              <Timer time={time} compensateCountdown={compensateCountdown} />
            </TimerContainer>
          )}
          {popupType !== "refill" && (
            <ActionButton
              onClick={popupType === "success" ? onDetails : closeModal}
            >
              {popupType === "compensate" ? (
                <Gettext>Cancel</Gettext>
              ) : (
                <Gettext>Details</Gettext>
              )}
            </ActionButton>
          )}
        </BottomContainer>
      </Container>
    </PopupLayout>
  );
});

export default Popup;



// WEBPACK FOOTER //
// ./src/components/Protection/Popup.js